import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { VirtualRows } from '../ui/virtual-rows';
import { useViewState } from '../ui/session-state';
import type { JSX } from "react";
import { Checkbox, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Select, Tooltip } from "@fluentui/react-components";
import { ArrowClockwiseRegular, ChevronDownRegular, ChevronRightRegular, ChevronUpRegular, ClipboardPasteRegular, CheckmarkRegular, MoreHorizontalRegular, PersonRegular } from "@fluentui/react-icons";
import { flushInputDrafts } from '../ui/input-drafts';
import { InventoryPaste } from '../ui/inventory-paste';
import { adjacentCell, inventoryFields, inventoryLabels, type EntryCell, type EntryOrder, type InventoryField } from '../resource-entry';
import { RESOURCE_GROUPS, backpackFragmentCount, itemFragmentCount, resourceGroupForCharacter, totalFragmentCount } from "@minglun/domain";
import type { Character, InventoryInput, ResourceGroup } from "@minglun/domain";
import { matchesAdvancedSearch } from "../search";
import { formatNumber, resourceGroupLabels, resourceGroupOptions, type PageProps } from "../workbench-shared";
import { GroupBadge, SearchBox, FilterMenu, NumberInput, NoResults } from "../ui/controls";

export function ResourcesPage({
  workbench,
  userPlan,
  setUserPlan,
  confirmAction,
  notify,
}: PageProps): JSX.Element {
  const root=useRef<HTMLDivElement>(null);
  const [order,setOrder]=useViewState<EntryOrder>('ResourcesPage.entryOrder','row');
  const [activeCell,setActiveCell]=useState<EntryCell>();
  const [pasting,setPasting]=useState(false);
  const [keyboardInset,setKeyboardInset]=useState(0);
  const [query, setQuery] = useViewState('ResourcesPage.query', "");
  const [collapsed,setCollapsed] = useViewState<string[]>('ResourcesPage.collapsed', []);
  const deferredQuery = useDeferredValue(query);
  const [groupFilter, setGroupFilter] = useViewState('ResourcesPage.groupFilter', "全部");
  const [onlyEntered, setOnlyEntered] = useViewState('ResourcesPage.onlyEntered', false);
  const inventory = useMemo(() => new Map(userPlan.inventory.map(item => [item.characterId,item])), [userPlan.inventory]);
  const isEntered = (characterId: string): boolean => {
    const item = inventory.get(characterId);
    return Boolean(item && (item.wheelCount > 0 || totalFragmentCount(item) > 0));
  };
  const ordered = useMemo(() => [...workbench.characters].sort((a,b)=>a.order-b.order), [workbench.characters]);
  const visible = ordered
    .filter((character) => {
      const resourceGroup = resourceGroupForCharacter(character);
      return (
        matchesAdvancedSearch(deferredQuery, {
          text: [
            character.name,
            character.id,
            resourceGroupLabels[resourceGroup],
            isEntered(character.id) ? "已录入" : "未录入",
          ],
          fields: {
            name: character.name,
            id: character.id,
            rarity: resourceGroupLabels[resourceGroup],
            status: isEntered(character.id) ? "已录入" : "未录入",
          },
          aliases: {
            名称: "name",
            命轮: "name",
            编号: "id",
            品质: "rarity",
            状态: "status",
          },
        }) &&
        (groupFilter === "全部" || resourceGroup === groupFilter) &&
        (!onlyEntered || isEntered(character.id))
      );
    });
  const update = useCallback((
    id: string,
    patch: Partial<Pick<InventoryInput, "wheelCount" | "backpackFragmentCount" | "itemFragmentCount" | "ignoreFragments">>,
  ): void =>
    setUserPlan((plan) => ({
      ...plan,
      inventory: plan.inventory.some((item) => item.characterId === id)
        ? plan.inventory.map((item) =>
            item.characterId === id ? { ...item, ...patch } : item,
          )
        : [
            ...plan.inventory,
            {
              characterId: id,
              wheelCount: patch.wheelCount ?? 0,
              backpackFragmentCount: patch.backpackFragmentCount ?? 0,
              itemFragmentCount: patch.itemFragmentCount ?? 0,
              ignoreFragments: patch.ignoreFragments ?? false,
            },
          ],
    })), [setUserPlan]);
  const hasResourceValues =
    userPlan.solver.ssrWheelChoices > 0 ||
    userPlan.inventory.some(
      (item) => item.wheelCount > 0 || totalFragmentCount(item) > 0 || item.ignoreFragments,
    );
  const resetResources = (): void => {
    if (!hasResourceValues) return;
    confirmAction({
      title: "重置资源录入？",
      message: "所有命轮、碎片和 SSR 命轮自选数量将恢复为 0。",
      confirmLabel: "重置",
      onConfirm: () =>
        setUserPlan((plan) => ({
          ...plan,
          inventory: plan.inventory.map((item) => ({
            ...item,
            wheelCount: 0,
            backpackFragmentCount: 0,
            itemFragmentCount: 0,
            ignoreFragments: false,
          })),
          solver: { ...plan.solver, ssrWheelChoices: 0, ignoredFragmentGroups: [] },
        })),
    });
  };
  const setGroupIgnored = (group: ResourceGroup, ignored: boolean): void => {
    const ids = new Set(workbench.characters
      .filter((character) => resourceGroupForCharacter(character) === group)
      .map((character) => character.id));
    setUserPlan((plan) => ({
      ...plan,
      inventory: [...plan.inventory.map((item) => ids.has(item.characterId) ? { ...item, ignoreFragments: ignored } : item), ...[...ids].filter(id=>!plan.inventory.some(item=>item.characterId===id)).map(characterId=>({characterId,wheelCount:0,backpackFragmentCount:0,itemFragmentCount:0,ignoreFragments:ignored}))],
      solver: { ...plan.solver, ignoredFragmentGroups: [] },
    }));
  };
  const resetFilters = (): void => {
    setQuery("");
    setGroupFilter("全部");
    setOnlyEntered(false);
  };
  const groups =
    groupFilter === "全部"
      ? [...RESOURCE_GROUPS]
      : [groupFilter as ResourceGroup];
  const entryIds=groups.flatMap(group => !deferredQuery.trim() && collapsed.includes(group) ? [] : visible.filter(c=>resourceGroupForCharacter(c)===group).map(c=>c.id));
  const focusCell=(cell:EntryCell) => {
    setActiveCell(cell);
    requestAnimationFrame(()=>{
      const article=Array.from(root.current?.querySelectorAll<HTMLElement>('[data-resource-id]') ?? []).find(e=>e.dataset.resourceId===cell.id);
      const input=article?.querySelector<HTMLInputElement>(`[data-resource-field="${cell.field}"] input`);
      input?.focus({preventScroll:true}); input?.select(); input?.scrollIntoView({block:'nearest',inline:'nearest'});
    });
  };
  const advance=(direction:number) => { if(!activeCell)return; flushInputDrafts(); const next=adjacentCell(entryIds,activeCell,direction,order); if(next)focusCell(next); else { (document.activeElement as HTMLElement)?.blur();setActiveCell(undefined); } };
  useEffect(()=>{
    const viewport=window.visualViewport;
    const measure=()=>setKeyboardInset(viewport?Math.max(0,innerHeight-viewport.height-viewport.offsetTop):0);
    viewport?.addEventListener('resize',measure);viewport?.addEventListener('scroll',measure);measure();
    return()=>{viewport?.removeEventListener('resize',measure);viewport?.removeEventListener('scroll',measure);};
  },[]);
  return (
    <div ref={root} className={`resource-page grouped-resources ${activeCell?'entry-active':''}`} data-config-section="resources" tabIndex={-1}
      onFocusCapture={event=>{const input=event.target as HTMLElement;const field=input.closest<HTMLElement>('[data-resource-field]')?.dataset.resourceField as InventoryField|undefined;const id=input.closest<HTMLElement>('[data-resource-id]')?.dataset.resourceId;if(field&&id)setActiveCell({id,field});}}
      onBlurCapture={()=>requestAnimationFrame(()=>{if(!root.current?.contains(document.activeElement))setActiveCell(undefined);})}
      onKeyDownCapture={event=>{if(event.key==='Enter' && (event.target as HTMLElement).closest('[data-resource-field]')){event.preventDefault();event.stopPropagation();advance(event.shiftKey?-1:1);}}}>
      <div className="resource-toolbar">

        <div className="toolbar-filters">
          <SearchBox
            value={query}
            onChange={setQuery}
            placeholder="搜索命轮"
            resultCount={visible.length}
            totalCount={workbench.characters.length}
          />
          <FilterMenu
            label="品质"
            icon={<PersonRegular />}
            value={groupFilter}
            options={resourceGroupOptions}
            onChange={setGroupFilter}
          />
          <Checkbox
            className="filter-checkbox"
            checked={onlyEntered}
            label="仅已录入"
            onChange={(_event, data) => setOnlyEntered(data.checked === true)}
          />
        </div>
        <div className="resource-toolbar-actions">
          <label className="entry-order"><span>录入</span><Select aria-label="资源录入顺序" value={order} onChange={(_,data)=>setOrder(data.value as EntryOrder)}><option value="row">逐项</option>{inventoryFields.map(field=><option key={field} value={field}>按{inventoryLabels[field]}</option>)}</Select></label>
          <button type="button" className="paste-launch" onClick={()=>{setActiveCell(undefined);setPasting(true);}}><ClipboardPasteRegular/><span>批量粘贴</span></button>
          <div
            className="objective-main-actions resource-main-actions"
            role="group"
            aria-label="资源录入操作"
          >
            <Tooltip content="重置资源录入" relationship="label">
              <button
                type="button"
                aria-label="重置资源录入"
                disabled={!hasResourceValues}
                onClick={resetResources}
              >
                <ArrowClockwiseRegular />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
      <div className="resource-board" data-scroll-id="resources" aria-label="资源录入">
        {groups.map(group => {
          const characters = visible.filter(character => resourceGroupForCharacter(character) === group);
          if (!characters.length) return null;
          const isCollapsed = !deferredQuery.trim() && collapsed.includes(group);
          return <section className="resource-lane" key={group} aria-label={`${resourceGroupLabels[group]}资源`}>
            <ResourceGroupRow group={group} characters={workbench.characters} inventory={inventory} visibleCount={characters.length} setGroupIgnored={setGroupIgnored} collapsed={isCollapsed} onToggle={() => setCollapsed(old => old.includes(group) ? old.filter(x => x !== group) : [...old, group])} />
            {!isCollapsed && <>
              <div className="resource-lane-columns" aria-hidden="true"><span>命轮数量</span><span>背包碎片</span><span>道具碎片</span></div>
              <VirtualRows items={characters} itemKey={character => character.id} estimate={82} pinnedKey={activeCell?.id}>
                {character => <ResourceEditor key={character.id} character={character} inventory={inventory.get(character.id) ?? {characterId:character.id,wheelCount:0,backpackFragmentCount:0,itemFragmentCount:0,ignoreFragments:false}} onChange={update} />}
              </VirtualRows>
            </>}
          </section>;
        })}
      </div>
      {activeCell && <div className="entry-assistant" style={{bottom:keyboardInset>100?`${keyboardInset}px`:undefined}} role="toolbar" aria-label="连续录入">
        <span><strong>{workbench.characters.find(c=>c.id===activeCell.id)?.name}</strong><small>{inventoryLabels[activeCell.field]}</small></span>
        <button type="button" aria-label="录入上一项" disabled={!adjacentCell(entryIds,activeCell,-1,order)} onPointerDown={e=>e.preventDefault()} onClick={()=>advance(-1)}><ChevronUpRegular/></button>
        <button type="button" aria-label="录入下一项" disabled={!adjacentCell(entryIds,activeCell,1,order)} onPointerDown={e=>e.preventDefault()} onClick={()=>advance(1)}><ChevronDownRegular/></button>
        <button type="button" className="entry-done" onClick={()=>{flushInputDrafts();(document.activeElement as HTMLElement)?.blur();setActiveCell(undefined);}}><CheckmarkRegular/>完成</button>
      </div>}
      {pasting && <InventoryPaste characters={workbench.characters} onClose={()=>setPasting(false)} onApply={rows=>{
        const patches=new Map(rows.map(row=>[row.characterId!,row.values!]));
        setUserPlan(plan=>({...plan,inventory:[...plan.inventory.map(item=>patches.has(item.characterId)?{...item,...patches.get(item.characterId)}:item),...rows.filter(row=>!plan.inventory.some(item=>item.characterId===row.characterId)).map(row=>({characterId:row.characterId!,...row.values!,ignoreFragments:false}))]}));
        setPasting(false);notify(`已录入 ${rows.length} 位伙伴，可撤销`);
      }} />}
      {!visible.length && (
        <NoResults
          label="没有符合条件的命轮"
          query={query}
          onClear={resetFilters}
        />
      )}
    </div>
  );
}

type InventoryPatch = Partial<Pick<InventoryInput, 'wheelCount'|'backpackFragmentCount'|'itemFragmentCount'|'ignoreFragments'>>;
const ResourceGroupRow = memo(function ResourceGroupRow({group,characters,inventory,visibleCount,setGroupIgnored,collapsed,onToggle}:{group:ResourceGroup;characters:Character[];inventory:Map<string,InventoryInput>;visibleCount:number;setGroupIgnored(group:ResourceGroup,ignored:boolean):void;collapsed:boolean;onToggle():void}) {
  const all=characters.filter(c=>resourceGroupForCharacter(c)===group);
  const ignored=all.filter(c=>inventory.get(c.id)?.ignoreFragments).length;
  return <div className="resource-group-row"><button type="button" className="resource-group-toggle" onClick={onToggle} aria-expanded={!collapsed} aria-label={`${resourceGroupLabels[group]}分组`}>{collapsed?<ChevronRightRegular/>:<ChevronDownRegular/>}<GroupBadge group={group}/><small>{visibleCount}</small></button><Menu><MenuTrigger disableButtonEnhancement><button className="row-menu" type="button" aria-label={`${resourceGroupLabels[group]}分组操作`}><MoreHorizontalRegular/></button></MenuTrigger><MenuPopover><MenuList><MenuItem onClick={()=>setGroupIgnored(group,true)}>本组不限制碎片</MenuItem><MenuItem onClick={()=>setGroupIgnored(group,false)}>本组恢复碎片限制</MenuItem></MenuList>{ignored > 0 && <p className="menu-explanation">本组 {ignored} 项不限制碎片</p>}</MenuPopover></Menu></div>;
});
export const ResourceEditor = memo(function ResourceEditor({character,inventory,onChange}:{character:Character;inventory:InventoryInput;onChange(id:string,patch:InventoryPatch):void}) {
  const backpack=backpackFragmentCount(inventory), items=itemFragmentCount(inventory);
  const ignored=inventory.ignoreFragments===true;
  return <article className={`resource-entry ${ignored?'fragments-ignored':''}`} aria-label={character.name} data-resource-id={character.id}>
    <div className="resource-entry-heading">
      <span className="resource-name" title={character.name}>{character.name}</span>
      <Menu><MenuTrigger disableButtonEnhancement><button className="row-menu" type="button" aria-label={`${character.name}资源操作`}><MoreHorizontalRegular/></button></MenuTrigger><MenuPopover><MenuList><MenuItem onClick={()=>onChange(character.id,{ignoreFragments:!ignored})}>{ignored?'恢复碎片限制':'不限制碎片'}</MenuItem></MenuList><p className="menu-explanation">用于规划，结果可能超过当前碎片数量。已录入数量会保留。</p></MenuPopover></Menu>
    </div>
    <div className="resource-entry-inputs">
      <span data-resource-field="wheelCount"><NumberInput value={inventory.wheelCount} min={0} ariaLabel={`${character.name}命轮`} onChange={wheelCount=>onChange(character.id,{wheelCount})}/></span>
      <span data-resource-field="backpackFragmentCount"><NumberInput value={backpack} min={0} ariaLabel={`${character.name}背包碎片`} onChange={backpackFragmentCount=>onChange(character.id,{backpackFragmentCount})}/></span>
      <span data-resource-field="itemFragmentCount"><NumberInput value={items} min={0} ariaLabel={`${character.name}道具碎片`} onChange={itemFragmentCount=>onChange(character.id,{itemFragmentCount})}/></span>
    </div>
    <div className="resource-entry-footer">{ignored && <small className="unlimited-label" title="用于规划；结果可能超过当前碎片数量">不限制碎片</small>}<span className="resource-sum" title="背包与道具碎片之和">合计 <output>{formatNumber(backpack+items,0)}</output></span></div>
  </article>;
});
